/**
 * Contexto Global para Gerenciamento de Heartbeats
 * 
 * Gerencia heartbeats de múltiplas OS de forma centralizada
 * Evita parada do heartbeat quando modais são fechados
 * 
 * @author Equipe GestaoeCRM
 * @date 2025-10-03
 */

import React, { createContext, useContext, useRef, useCallback, ReactNode } from 'react';
import { updateHeartbeat } from '../services/activeOSApi';

// ============================================================================
// INTERFACES
// ============================================================================

interface HeartbeatContextType {
  startHeartbeat: (osId: string) => void;
  stopHeartbeat: (osId: string) => void;
  isHeartbeatRunning: (osId: string) => boolean;
}

interface HeartbeatContextProviderProps {
  children: ReactNode;
}

// ============================================================================
// CONTEXTO
// ============================================================================

const HeartbeatContext = createContext<HeartbeatContextType | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

export function HeartbeatProvider({ children }: HeartbeatContextProviderProps) {
  // Refs para controlar heartbeats de múltiplas OS
  const heartbeatsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const isRunningRef = useRef<Map<string, boolean>>(new Map());

  /**
   * Enviar heartbeat para uma OS específica
   */
  const sendHeartbeat = useCallback(async (osId: string) => {
    try {
      await updateHeartbeat(osId);
      // console.log(`[HeartbeatContext] Heartbeat enviado para OS: ${osId}`);
    } catch (error) {
      // console.warn(`[HeartbeatContext] Erro no heartbeat para OS ${osId}:`, error);
    }
  }, []);

  /**
   * Iniciar heartbeat para uma OS
   */
  const startHeartbeat = useCallback((osId: string) => {
    // Verificar se já está rodando
    if (isRunningRef.current.get(osId)) {
      // console.log(`[HeartbeatContext] Heartbeat já está rodando para OS: ${osId}`);
      return;
    }

    // Limpar heartbeat anterior se existir
    const existingInterval = heartbeatsRef.current.get(osId);
    if (existingInterval) {
      clearInterval(existingInterval);
    }

    // Marcar como rodando
    isRunningRef.current.set(osId, true);

    // Enviar primeiro heartbeat imediatamente
    sendHeartbeat(osId);

    // Configurar intervalo de 30 segundos (otimizado para evitar rate limiting)
    const interval = setInterval(() => {
      sendHeartbeat(osId);
    }, 30000);

    // Salvar referência do intervalo
    heartbeatsRef.current.set(osId, interval);

    // console.log(`[HeartbeatContext] Heartbeat iniciado para OS: ${osId}`);
  }, [sendHeartbeat]);

  /**
   * Parar heartbeat para uma OS
   */
  const stopHeartbeat = useCallback((osId: string) => {
    // Verificar se está rodando
    if (!isRunningRef.current.get(osId)) {
      // console.log(`[HeartbeatContext] Heartbeat não está rodando para OS: ${osId}`);
      return;
    }

    // Limpar intervalo
    const interval = heartbeatsRef.current.get(osId);
    if (interval) {
      clearInterval(interval);
      heartbeatsRef.current.delete(osId);
    }

    // Marcar como parado
    isRunningRef.current.set(osId, false);

    // console.log(`[HeartbeatContext] Heartbeat parado para OS: ${osId}`);
  }, []);

  /**
   * Verificar se heartbeat está rodando para uma OS
   */
  const isHeartbeatRunning = useCallback((osId: string) => {
    return isRunningRef.current.get(osId) || false;
  }, []);

  // Cleanup ao desmontar
  React.useEffect(() => {
    return () => {
      // Limpar todos os heartbeats
      heartbeatsRef.current.forEach((interval) => {
        clearInterval(interval);
      });
      heartbeatsRef.current.clear();
      isRunningRef.current.clear();
    };
  }, []);

  const value: HeartbeatContextType = {
    startHeartbeat,
    stopHeartbeat,
    isHeartbeatRunning,
  };

  return (
    <HeartbeatContext.Provider value={value}>
      {children}
    </HeartbeatContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

export function useHeartbeat() {
  const context = useContext(HeartbeatContext);
  if (context === undefined) {
    throw new Error('useHeartbeat deve ser usado dentro de um HeartbeatProvider');
  }
  return context;
}

export default HeartbeatContext;
